import React from "react";
import { Box, type BoxProps, chakra, Flex, Icon, Text } from "@chakra-ui/react";
import { useReducedMotion } from "../../theme/foundations/motion";
import { motion } from "framer-motion";
import type { IconType } from "react-icons";
import { CountUp } from "./CountUp";
import { StatSkeleton } from "./Skeletons";
import { type Tone, toneTokens } from "./tones";

export type { Tone } from "./tones";

export interface StatCardProps extends Omit<BoxProps, "onClick"> {
  /** Eyebrow label, e.g. "Overdue balance". */
  label: string;
  /** Numbers count up (through `format`); strings render verbatim (e.g. terms.text). */
  value: number | string;
  /** formatAmount / formatQuantity — applied during and after the count-up. */
  format?: (n: number) => string;
  /** Currency eyebrow before the value, e.g. "AED". */
  unit?: string;
  caption?: React.ReactNode;
  icon?: IconType;
  /** Colour of the icon blob / selection ring; danger also colours the value. Default neutral. */
  tone?: Tone;
  /** "kpi" → display.sm; "hero" → display.lg + a green hairline drawn beneath the numeral. */
  size?: "kpi" | "hero";
  /** "card" (static) · "tile" (selectable filter, aria-pressed) · "action" (whole card is a button). */
  variant?: "card" | "tile" | "action";
  isSelected?: boolean;
  onClick?: () => void;
  /** Top-right slot, usually a <StatusBadge>. */
  badge?: React.ReactNode;
  /** Renders a StatSkeleton of the same size. */
  isLoading?: boolean;
  /** Overrides the value's textStyle, e.g. "h3" for a name rather than a number. Default follows `size`. */
  valueTextStyle?: string;
}

// Numerals step down one size below lg / md so a 12-character amount never breaks mid-number in a 3-up grid.
// Convenience text styles from the theme that carry their own breakpoints.
const HERO_VALUE_STYLE = "stat.hero";
const KPI_VALUE_STYLE = "stat.kpi";

const MotionBox = motion(Box);

/**
 * KPI / hero / selectable tile / action card built on layerStyle="card".
 * @example <StatCard size="hero" tone={overdue > 0 ? "danger" : "success"} label="Overdue balance" unit={currency} value={overdue} format={formatAmount} caption="No overdue amount" badge={<StatusBadge kind="application" value="clear" />} />
 * @example <StatCard variant="tile" tone="warning" icon={FiClock} label="Running low" value={counts.low} caption="Reorder within a week" isSelected={filter === "low"} onClick={() => setFilter("low")} />
 * @example <StatCard variant="action" icon={FiMessageCircle} label="Raise a ticket" value="We reply within one working day" onClick={onOpen} />
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  format,
  unit,
  caption,
  icon,
  tone = "neutral",
  size = "kpi",
  variant = "card",
  isSelected = false,
  onClick,
  badge,
  isLoading = false,
  valueTextStyle,
  ...rest
}) => {
  const reduce = useReducedMotion();
  if (isLoading) return <StatSkeleton size={size} />;

  const tokens = toneTokens(tone);
  const interactive = variant === "tile" || variant === "action";
  const isHero = size === "hero";
  const valueColor = tone === "danger" ? "text.danger" : "text.primary";

  // Spans throughout (display set explicitly): tile/action variants render this inside a <button>, which only allows phrasing content.
  const content = (
    <>
      <Flex as="span" display="flex" align="center" justify="space-between" gap={3} mb={isHero ? 4 : 3}>
        <Flex as="span" display="flex" align="center" gap={3} minW={0}>
          {icon && (
            <Box
              as="span"
              w="36px"
              h="36px"
              borderRadius="full"
              bg={tokens.bg}
              color={tokens.solid}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              aria-hidden
            >
              <Icon as={icon} boxSize="18px" />
            </Box>
          )}
          <Text as="span" textStyle="label" color="text.muted" noOfLines={variant === "tile" ? 2 : 1}>
            {label}
          </Text>
        </Flex>
        {badge && (
          <Box as="span" display="block" flexShrink={0}>
            {badge}
          </Box>
        )}
      </Flex>

      <Flex as="span" display="flex" align="baseline" gap="0.4em" wrap="wrap">
        {unit && (
          <Text as="span" textStyle="label" color="text.muted">
            {unit}
          </Text>
        )}
        <Text
          as="span"
          display="block"
          textStyle={valueTextStyle ?? (isHero ? HERO_VALUE_STYLE : KPI_VALUE_STYLE)}
          color={valueColor}
          // Numbers never break mid-figure; strings (e.g. payment terms) wrap instead of overflowing the card.
          whiteSpace={!valueTextStyle && typeof value === "number" ? "nowrap" : undefined}
          overflowWrap={typeof value === "string" ? "anywhere" : undefined}
        >
          {typeof value === "number" ? <CountUp value={value} format={format} /> : value}
        </Text>
      </Flex>

      {isHero && (
        <MotionBox
          as="span"
          display="block"
          aria-hidden
          h="1px"
          bg="leaf.500"
          mt={3}
          style={{ transformOrigin: "left center" }}
          initial={reduce ? false : { scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
      )}

      {caption && (
        <Text as="span" display="block" textStyle="caption" color="text.muted" mt={isHero ? 3 : 2}>
          {caption}
        </Text>
      )}
    </>
  );

  const selectedRing = isSelected ? `0 0 0 2px var(--chakra-colors-${tokens.solid.replace(/\./g, "-")})` : undefined;
  const selectedStyles = selectedRing ? { boxShadow: selectedRing, bg: tokens.bg } : {};
  // Hover lifts with the md shadow; a selected card keeps its tone ring underneath it.
  const hoverShadow = selectedRing ? `${selectedRing}, var(--chakra-shadows-md)` : "md";

  if (interactive) {
    return (
      <chakra.button
        type="button"
        onClick={onClick}
        aria-pressed={variant === "tile" ? isSelected : undefined}
        layerStyle="card"
        p={5}
        w="full"
        textAlign="left"
        display="block"
        position="relative"
        minH="44px"
        cursor="pointer"
        transition="transform 160ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 160ms cubic-bezier(0.2,0.8,0.2,1), background-color 120ms"
        _hover={{ boxShadow: hoverShadow, transform: reduce ? undefined : "translateY(-2px)" }}
        _active={{ transform: "translateY(0)" }}
        _focusVisible={{ boxShadow: "outline" }}
        {...selectedStyles}
        {...(rest as React.ComponentProps<typeof chakra.button>)}
      >
        {content}
      </chakra.button>
    );
  }

  return (
    <Box layerStyle="card" p={5} position="relative" {...selectedStyles} {...rest}>
      {content}
    </Box>
  );
};
